import { useState } from "react";
import { Link } from "react-router-dom";
import { Receipt, Loader2, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-xl shadow-sm p-8">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="h-10 w-10 rounded-lg bg-blue-600 flex items-center justify-center">
            <Receipt className="h-5 w-5 text-white" strokeWidth={2.2} />
          </div>
          <div className="font-heading font-semibold text-slate-900 text-lg">Tax Flow</div>
        </div>

        {sent ? (
          <div className="text-center" data-testid="forgot-password-sent">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
            <h1 className="font-heading text-xl font-semibold text-slate-900">Check your email</h1>
            <p className="text-slate-500 text-sm mt-2">
              If an account exists for <span className="font-medium">{email}</span>, a password reset link has been sent.
            </p>
            <Link to="/login" className="inline-flex items-center gap-1.5 text-blue-600 text-sm mt-6 hover:underline">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-xl font-semibold text-slate-900">Forgot password?</h1>
            <p className="text-slate-500 text-sm mt-1.5 mb-6">Enter your email and we'll send you a reset link.</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@expattaxfirm.com" data-testid="forgot-email"
                  className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {error && <div className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">{error}</div>}
              <button type="submit" disabled={loading} data-testid="forgot-submit"
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg px-4 py-2.5 font-medium flex items-center justify-center gap-2">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} Send reset link
              </button>
            </form>
            <Link to="/login" className="inline-flex items-center gap-1.5 text-slate-500 text-sm mt-6 hover:text-slate-700">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
